import { cva } from 'class-variance-authority'

import { TextSize } from '#components/text/types'

import { ButtonSize, ButtonVariant } from './types'

/** Text fade-in animation for label changes */
export const TEXT_FADE_IN_ANIMATION =
  'var(--animate-fade-in) 0.3s var(--ease-out-quart) forwards'

/** Width transition for animateWidth prop. */
export const WIDTH_TRANSITION =
  'width 200ms var(--default-transition-timing-function)'

/** Maps button size to text size */
export const buttonSizeToTextSize: Record<ButtonSize, TextSize> = {
  [ButtonSize.Small]: TextSize.Small,
  [ButtonSize.Medium]: TextSize.Medium,
  [ButtonSize.Large]: TextSize.Medium,
}

/**
 * Corner smoothing: superellipse(1.5) ≈ 30% Figma smoothing.
 * This makes corners appear smaller, so we compensate by increasing
 * border-radius from rounded-md (6px) to rounded-lg (8px) when supported.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/Reference/Properties/corner-shape
 */
export const buttonVariants = cva(
  [
    // Layout
    'relative inline-flex items-center justify-center whitespace-nowrap gap-1.5',
    // Elevation shadow slot (currently transparent; keeps box-shadow lists valid)
    '[--button-shadow-elevation:0_0_0_0_transparent]',
    // Border radius
    'rounded-md',
    // Corner smoothing (not well supported yet)
    //'supports-[corner-shape:round]:rounded-lg supports-[corner-shape:round]:[corner-shape:superellipse(1.5)]',
    // Cursor
    'hover:cursor-pointer',
    // Icon-only: square when no text label (icon via leftSection/rightSection)
    '[&:not(:has([data-label]))]:aspect-square [&:not(:has([data-label]))]:p-0.5',
    // Icon-only: square when label contains only an SVG (icon as children)
    '[&:has([data-label]>svg:only-child)]:aspect-square [&:has([data-label]>svg:only-child)]:p-0.5',
    // Disabled
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-disabled',
    // Focus (pseudo-element to preserve button's box-shadow)
    'focus-visible:outline-none',
    'before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:transition-shadow before:duration-175 before:ease',
  ],
  {
    defaultVariants: {
      size: ButtonSize.Medium,
      variant: ButtonVariant.Primary,
    },
    variants: {
      size: {
        [ButtonSize.Small]: 'h-6 min-w-6 px-1.75 py-0',
        [ButtonSize.Medium]: 'h-7 min-w-7 px-2.25 py-0.5',
        [ButtonSize.Large]: 'h-8 min-w-8 px-2.75 py-1',
      },
      variant: {
        [ButtonVariant.Destructive]: [
          // Interaction mode
          'interactive dark:stronger light:inverted-stronger',
          // Background
          'bg-surface-button-destructive',
          // Border (via box-shadow)
          '[box-shadow:0_0.5px_0_0_var(--color-border-button-destructive-top)_inset,0_0_0_0.5px_var(--color-border-button-destructive-inset),var(--button-shadow-elevation)]',
          // Text
          'text-text-inverse',
          // Focus (adds ring to existing border shadow)
          'focus-visible:[box-shadow:0_0.5px_0_0_var(--color-border-button-destructive-top)_inset,0_0_0_0.5px_var(--color-border-button-destructive-inset),var(--button-shadow-elevation),var(--shadow-focus-ring-danger)]',
          // In dark mode the border matches the ring, so it steps a shade darker while focused
          'dark:focus-visible:[box-shadow:0_0.5px_0_0_var(--color-border-button-destructive-top)_inset,0_0_0_0.5px_var(--color-red-10),var(--button-shadow-elevation),var(--shadow-focus-ring-danger)]',
        ],
        [ButtonVariant.Success]: [
          // Interaction mode
          'interactive dark:stronger light:inverted-stronger',
          // Background
          'bg-surface-button-success',
          // Border (via box-shadow)
          '[box-shadow:0_0.5px_0_0_var(--color-border-button-success-top)_inset,0_0_0_0.5px_var(--color-border-button-success-inset),var(--button-shadow-elevation)]',
          // Text
          'text-text-inverse',
          // Focus (adds ring to existing border shadow)
          'focus-visible:[box-shadow:0_0.5px_0_0_var(--color-border-button-success-top)_inset,0_0_0_0.5px_var(--color-border-button-success-inset),var(--button-shadow-elevation),var(--shadow-focus-ring-success)]',
          'dark:focus-visible:[box-shadow:0_0.5px_0_0_var(--color-border-button-success-top)_inset,0_0_0_0.5px_var(--color-green-10),var(--button-shadow-elevation),var(--shadow-focus-ring-success)]',
        ],
        [ButtonVariant.Ghost]: [
          // Interaction mode
          'interactive',
          // Background
          'bg-transparent',
          // Border
          'border-0',
          // Text
          'text-text-primary',
          // Focus (composed: ring + inset border, different inset color in dark mode)
          'focus-visible:before:[box-shadow:var(--shadow-focus-ring-subtle),0_0_0_0.5px_var(--color-border-button-secondary-inset)_inset]',
          'dark:focus-visible:before:[box-shadow:var(--shadow-focus-ring-subtle),0_0_0_0.5px_var(--color-border-button-secondary-top)_inset]',
        ],
        [ButtonVariant.Primary]: [
          // Interaction mode
          'interactive light:inverted-stronger',
          // Background
          'bg-surface-button-primary',
          // Border (via box-shadow)
          '[box-shadow:0_0.5px_0_0_var(--color-border-button-default-top)_inset,0_0_0_0.5px_var(--color-border-button-default-inset),var(--button-shadow-elevation)]',
          // Text
          'text-text-inverse',
          // Focus (adds ring to existing border shadow)
          'focus-visible:[box-shadow:0_0.5px_0_0_var(--color-border-button-default-top)_inset,0_0_0_0.5px_var(--color-border-button-default-inset),var(--button-shadow-elevation),var(--shadow-focus-ring)]',
        ],
        [ButtonVariant.Secondary]: [
          // Interaction mode
          'interactive',
          // Background
          'bg-surface-button-secondary',
          // Border (via box-shadow)
          '[box-shadow:0_-0.5px_0_0_var(--color-border-button-secondary-bottom)_inset,0_0_0_0.5px_var(--color-border-button-secondary-inset),var(--button-shadow-elevation)]',
          'dark:[box-shadow:0_-0.5px_0_0_var(--color-border-button-secondary-bottom)_inset,0_0_0_0.5px_var(--color-border-button-secondary-top),var(--button-shadow-elevation)]',
          // Text
          'text-text-primary',
          // Active state, light mode
          'light:active:not-disabled:not-data-[disabled]:not-data-[no-active-flash]:[box-shadow:0_-0.5px_0_0_var(--color-border-button-secondary-bottom)_inset,0_0_0_0.5px_var(--color-border-button-secondary-inset),var(--button-shadow-elevation),inset_0_1px_3px_var(--color-opacity-10)]',
          // Focus
          'focus-visible:before:shadow-focus-ring-subtle',
        ],
      },
    },
  },
)
